import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, Car, FileText, Calendar, Handshake, ArrowRight, MessageCircle, Star, Instagram, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { api, resolveContact } from "@/lib/api";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { buildWhatsAppLink } from "@/lib/format";
import { toInstagramEmbed } from "@/lib/instagram";

const BRANDS = ["Maruti Suzuki", "Hyundai", "Honda", "Tata", "Mahindra", "Kia", "Toyota", "BMW"];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  const [insta, setInsta] = useState([]);
  const [ri, setRi] = useState(0);
  const igRef = useRef(null);

  useEffect(() => {
    api.get("/vehicles?certified=true&limit=8&sort=year_desc").then((r) => setFeatured(r.data.items));
    api.get("/vehicles?limit=5&sort=newest&status=available").then((r) => setLatest(r.data.items));
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/reviews").then((r) => setReviews(r.data.items || []));
    api.get("/instagram").then((r) => setInsta(r.data.items || []));
  }, []);

  const { whatsapp } = resolveContact(settings);
  const scrollIg = (d) => igRef.current?.scrollBy({ left: d * 320, behavior: "smooth" });
  const instagramUrl = settings.instagram || `https://instagram.com/${settings.instagram_handle || "carzswag"}`;

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden bg-[#111]">
        <div className="absolute inset-0 opacity-55">
          <img src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=2000" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        </div>
        <div className="relative cs-container py-20 lg:py-28">
          <div className="max-w-2xl">
            <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Buy & Sell Used Cars <br /><span style={{ color: "#fff" }}>With</span> <span style={{ color: "#940C2C" }}>Confidence.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
              Hand-inspected pre-owned cars from a trusted Delhi dealership. Transparent pricing, verified history, and real after-sales support.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/buy" className="btn-primary" data-testid="hero-browse-cta"><Search size={16} /> Buy Cars</Link>
              <Link to="/sell" className="rounded-lg px-5 py-3 font-semibold border-2 border-white text-white hover:bg-white/10" data-testid="hero-sell-cta">Sell Car</Link>
              <a href={buildWhatsAppLink(whatsapp, "Hi Carz Swag!")} target="_blank" rel="noreferrer" className="btn-whatsapp" data-testid="hero-whatsapp-cta"><MessageCircle size={16} /> WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      {/* JUST ARRIVED */}
      {latest.length > 0 && (
        <section className="cs-container py-14">
          <div className="flex items-end justify-between">
            <SectionHead eyebrow="Fresh on the lot" title="Just Arrived This Week" />
            <Link to="/buy?sort=newest" className="hidden sm:inline-flex text-sm font-semibold text-[#940C2C]">View All →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mt-6">
            {latest.map((v) => <VehicleCard key={v.id} vehicle={v} compact />)}
          </div>
        </section>
      )}

      {/* REVIEWS SLIDER */}
      <section className="cs-container py-14">
        <SectionHead eyebrow="Trusted by Delhi drivers" title="What our customers say" />
        {reviews.length === 0 ? (
          <div className="mt-6 border border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-500 text-sm" data-testid="reviews-empty">
            Reviews from Carz Swag customers will appear here. Admin can add verified reviews from the dashboard.
          </div>
        ) : (
          <div className="mt-6">
            <div className="relative border border-gray-200 rounded-2xl p-8 bg-white">
              <div className="flex items-center gap-1 text-[#F59E0B]">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className={i < (reviews[ri]?.rating || 0) ? "fill-[#F59E0B]" : "text-gray-200"} />)}
              </div>
              <p className="mt-4 text-lg text-gray-800 leading-relaxed">"{reviews[ri]?.review}"</p>
              <div className="mt-5 text-sm">
                <div className="font-display font-bold text-gray-900">{reviews[ri]?.customer_name}</div>
                {reviews[ri]?.vehicle_purchased && <div className="text-gray-500">{reviews[ri].vehicle_purchased}</div>}
              </div>
              <div className="mt-6 flex gap-2">
                {reviews.map((_, i) => (
                  <button key={i} onClick={() => setRi(i)} className={`h-2 rounded-full transition-all ${i === ri ? "w-8 bg-[#940C2C]" : "w-2 bg-gray-200"}`} />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* HOW CARZ SWAG WORKS */}
      <section className="bg-[#F5F5F5] py-16">
        <div className="cs-container">
          <SectionHead eyebrow="A simple, trusted process" title="How Carz Swag Works" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: ShieldCheck, t: "Inspected", d: "Every certified car is personally inspected by our team." },
              { icon: Car, t: "Find Your Car", d: "Browse and filter inventory to find the right match." },
              { icon: Calendar, t: "Test Drive", d: "Book a visit — drive before you decide." },
              { icon: FileText, t: "Easy Financing", d: "We connect you with trusted finance partners." },
              { icon: Handshake, t: "Buy With Confidence", d: "Transparent paperwork and real after-sales support." },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all">
                <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ background: "#F6E6EB" }}>
                  <s.icon size={20} className="text-[#940C2C]" />
                </div>
                <div className="mt-4 font-display font-bold text-gray-900">{s.t}</div>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/buy" className="btn-primary" data-testid="how-browse-cars">Browse Cars <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="cs-container py-14">
        <div className="flex items-end justify-between">
          <SectionHead eyebrow="Hand-picked for you" title="Featured Certified Cars" />
          <Link to="/buy?certified=true" className="hidden sm:inline-flex text-sm font-semibold text-[#940C2C]">View All →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-6">
          {featured.slice(0, 8).map((v) => <VehicleCard key={v.id} vehicle={v} />)}
        </div>
      </section>

      <section className="cs-container pb-10">
        <SectionHead eyebrow="Trusted Brands" title="Popular Brands at Carz Swag" />
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 mt-6">
          {BRANDS.map((b) => (<Link key={b} to={`/buy?make=${encodeURIComponent(b)}`} className="border border-gray-200 rounded-lg p-4 text-center text-sm font-semibold text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">{b}</Link>))}
        </div>
      </section>

      <section className="cs-container pb-16">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#940C2C] to-[#6a0820] p-10 lg:p-14 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl">Have a car to sell?</h2>
              <p className="mt-3 text-white/90 max-w-md">Answer a few quick questions. Our team will review your car and share an honest offer.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/sell" className="bg-white text-[#940C2C] rounded-lg px-6 py-3 font-bold hover:bg-[#F6E6EB] transition-colors" data-testid="banner-sell-cta">Sell Your Car →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM SHOWCASE */}
      <section className="cs-container pb-16" data-testid="instagram-section">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#940C2C] inline-flex items-center gap-1.5"><Instagram size={12} /> @{settings.instagram_handle || "carzswag"}</span>
            <h2 className="mt-2 font-display font-extrabold text-3xl lg:text-4xl text-gray-900">Latest from Carz Swag</h2>
            <p className="mt-2 text-sm text-gray-500 max-w-lg">Follow us for new arrivals, car updates, deals and everything Carz Swag.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scrollIg(-1)} className="h-10 w-10 rounded-full border border-gray-200 flex items-center justify-center hover:border-gray-900 transition-colors" data-testid="ig-prev"><ChevronLeft size={16} /></button>
            <button onClick={() => scrollIg(1)} className="h-10 w-10 rounded-full border border-gray-200 flex items-center justify-center hover:border-gray-900 transition-colors" data-testid="ig-next"><ChevronRight size={16} /></button>
            <a href={instagramUrl} target="_blank" rel="noreferrer" className="btn-primary ml-2" data-testid="ig-follow-btn">
              <Instagram size={14} /> Follow Us
            </a>
          </div>
        </div>

        {insta.length === 0 ? (
          <div className="mt-6 border border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-500 text-sm" data-testid="instagram-empty">
            Instagram reels and posts from Carz Swag will appear here. Admin can add posts from the dashboard.
          </div>
        ) : (
          <div ref={igRef} className="mt-6 flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2">
            {insta.map((p) => {
              const embed = toInstagramEmbed(p.url);
              return (
                <div key={p.id} className="group shrink-0 w-72 snap-start rounded-xl overflow-hidden border border-gray-200 bg-white hover:shadow-lg transition-all" data-testid={`ig-card-${p.id}`}>
                  <div className="relative aspect-[9/16] bg-black overflow-hidden">
                    {embed && !p.thumbnail ? (
                      <iframe
                        title={p.title || p.vehicle_name || "Instagram reel"}
                        src={embed}
                        className="absolute inset-0 w-full h-full"
                        allow="encrypted-media"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        loading="lazy"
                      />
                    ) : (
                      <a href={p.url} target="_blank" rel="noreferrer" className="block absolute inset-0">
                        {p.thumbnail
                          ? <img src={p.thumbnail} alt={p.title || "Instagram"} className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                          : <div className="w-full h-full flex items-center justify-center text-white"><Instagram size={48} className="opacity-60" /></div>}
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1">
                          <Instagram size={10} /> Reel
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                          <div className="h-12 w-12 rounded-full bg-white/95 flex items-center justify-center"><Play size={20} className="text-[#940C2C] ml-0.5" fill="currentColor" /></div>
                        </div>
                      </a>
                    )}
                  </div>
                  {(p.title || p.vehicle_name) && (
                    <a href={p.url} target="_blank" rel="noreferrer" className="block p-3 hover:bg-gray-50">
                      {p.title && <div className="font-bold text-gray-900 text-sm line-clamp-1">{p.title}</div>}
                      {p.vehicle_name && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.vehicle_name}</div>}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

const SectionHead = ({ eyebrow, title }) => (
  <div>
    <span className="text-xs font-semibold uppercase tracking-widest text-[#940C2C]">{eyebrow}</span>
    <h2 className="mt-2 font-display font-extrabold tracking-tight text-3xl lg:text-4xl text-gray-900">{title}</h2>
  </div>
);
